import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { FeatureCards } from "./FeatureCards";

interface AddInterfaceModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddInterfaceModelDialog = ({
  open,
  onOpenChange,
}: AddInterfaceModelDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedBusinessType, setSelectedBusinessType] = useState("");

  const { data: businessTypes } = useQuery({
    queryKey: ["businessTypes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_types")
        .select("*");
      if (error) throw error;
      return data;
    },
  });

  const commerceLayout = {
    sections: [
      {
        name: "Gestion des Ventes",
        features: [
          {
            name: "Ventes Directes",
            description: "Gérez vos ventes au comptant"
          },
          {
            name: "Ventes à Crédit",
            description: "Gérez vos ventes à crédit avec suivi des paiements"
          }
        ]
      },
      {
        name: "Gestion des Stocks",
        features: [
          {
            name: "Inventaire",
            description: "Suivez votre stock en temps réel"
          },
          {
            name: "Approvisionnements",
            description: "Gérez vos approvisionnements"
          },
          {
            name: "Pertes",
            description: "Enregistrez les pertes de stock"
          }
        ]
      },
      {
        name: "Comptabilité",
        features: [
          {
            name: "Caisse",
            description: "Gérez votre caisse quotidienne"
          },
          {
            name: "Rapports",
            description: "Générez des rapports de ventes"
          }
        ]
      }
    ]
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("interface_models")
        .insert({
          name,
          description,
          business_type_id: selectedBusinessType,
          layout: commerceLayout,
        });

      if (error) throw error;

      toast.success("Modèle d'interface créé avec succès");
      onOpenChange(false);
      setName("");
      setDescription("");
      setSelectedBusinessType("");
    } catch (error) {
      console.error("Erreur lors de la création:", error);
      toast.error("Erreur lors de la création du modèle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[725px]">
        <DialogHeader>
          <DialogTitle>Ajouter un modèle d'interface</DialogTitle>
          <DialogDescription>
            Créez un nouveau modèle d'interface pour un type d'entreprise
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du modèle</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Entrez le nom du modèle"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Décrivez le modèle d'interface"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="businessType">Type d'entreprise</Label>
              <select
                id="businessType"
                value={selectedBusinessType}
                onChange={(e) => setSelectedBusinessType(e.target.value)}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Sélectionnez un type d'entreprise</option>
                {businessTypes?.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="submit">Créer le modèle</Button>
            </DialogFooter>
          </form>

          <Card>
            <CardContent className="p-4">
              <h3 className="text-lg font-semibold mb-4">Aperçu du modèle</h3>
              <div className="space-y-4">
                <FeatureCards sections={commerceLayout.sections} />
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};